import { apiDownload, apiFetch } from "./client";
import { query } from "./moodle";
import type {
  AttemptDetail,
  AttemptPage,
  AttemptStatus,
  CaseJobResponse,
  CaseSection,
  CaseTemplate,
  GenerateScenarioInput,
  Scenario,
  ScenarioInput,
  ScenarioPage,
  ScenarioReport,
  ScenarioStatus,
  SimSummary,
} from "./types";

// All /sim-patient/admin/* routes require admin, super_admin or root
// (app/api/v1/sim_patient_admin.py). Learners only ever see published
// scenarios; drafts and archived ones 404 on start.

const BASE = "/sim-patient/admin";

// --- scenarios ---------------------------------------------------------------

export function listScenarios(opts: {
  status?: ScenarioStatus;
  courseMoodleId?: number;
  q?: string;
  offset?: number;
  limit?: number;
} = {}) {
  return apiFetch<ScenarioPage>(
    `${BASE}/scenarios${query({
      status: opts.status,
      course_moodle_id: opts.courseMoodleId,
      q: opts.q,
      offset: opts.offset,
      limit: opts.limit,
    })}`
  );
}

export function getScenario(id: number) {
  return apiFetch<Scenario>(`${BASE}/scenarios/${id}`);
}

export function createScenario(body: ScenarioInput) {
  return apiFetch<Scenario>(`${BASE}/scenarios`, { method: "POST", body });
}

// Queues the Celery worker to fill the AI Simulated Patient Case Template
// from the course's approved content and save it as a draft (a minute or
// two). Poll GET /tasks/{task_id}: PROGRESS carries SimCaseProgress, SUCCESS
// carries { scenario_id }. Checks that don't need the LLM (unknown course,
// no approved content) fail right here with 422.
export function generateScenario(body: GenerateScenarioInput) {
  return apiFetch<CaseJobResponse>(`${BASE}/scenarios/generate`, { method: "POST", body });
}

// Saves an edited case; the playable scenario (persona, decision points,
// rubric) is rebuilt from it on the server.
export function updateCase(id: number, body: CaseTemplate) {
  return apiFetch<Scenario>(`${BASE}/scenarios/${id}/case`, { method: "PUT", body });
}

// Queued like generateScenario. No section regenerates the whole case
// (reviewer metadata is kept).
export function regenerateCase(id: number, opts: { section?: CaseSection; instructions?: string } = {}) {
  return apiFetch<CaseJobResponse>(`${BASE}/scenarios/${id}/regenerate`, {
    method: "POST",
    body: { section: opts.section ?? null, instructions: opts.instructions || null },
  });
}

// Hand-authored scenarios only; generated ones are edited through updateCase.
// Decision points are matched by id; any left out are deleted. Bumps the
// scenario's version when learners have already attempted it.
export function updateScenario(id: number, body: ScenarioInput) {
  return apiFetch<Scenario>(`${BASE}/scenarios/${id}`, { method: "PUT", body });
}

// 422 carries one error string per validation problem.
export function publishScenario(id: number) {
  return apiFetch<Scenario>(`${BASE}/scenarios/${id}/publish`, { method: "POST" });
}

export function unpublishScenario(id: number) {
  return apiFetch<Scenario>(`${BASE}/scenarios/${id}/unpublish`, { method: "POST" });
}

export function archiveScenario(id: number) {
  return apiFetch<Scenario>(`${BASE}/scenarios/${id}/archive`, { method: "POST" });
}

export function duplicateScenario(id: number) {
  return apiFetch<Scenario>(`${BASE}/scenarios/${id}/duplicate`, { method: "POST" });
}

// 409 once the scenario has attempts: archive it instead.
export function deleteScenario(id: number) {
  return apiFetch<{ deleted: boolean; id: number }>(`${BASE}/scenarios/${id}`, { method: "DELETE" });
}

export function getScenarioReport(id: number, since?: string) {
  return apiFetch<ScenarioReport>(`${BASE}/scenarios/${id}/report${query({ since })}`);
}

// --- attempts ----------------------------------------------------------------

export interface AttemptFilters {
  scenarioId?: number;
  status?: AttemptStatus;
  userId?: number;
  since?: string;
}

function attemptQuery(f: AttemptFilters) {
  return { scenario_id: f.scenarioId, status: f.status, user_id: f.userId, since: f.since };
}

export function listAttempts(opts: AttemptFilters & { offset?: number; limit?: number } = {}) {
  return apiFetch<AttemptPage>(
    `${BASE}/attempts${query({ ...attemptQuery(opts), offset: opts.offset, limit: opts.limit })}`
  );
}

export function getAttempt(id: number) {
  return apiFetch<AttemptDetail>(`${BASE}/attempts/${id}`);
}

export function abandonAttempt(id: number) {
  return apiFetch<AttemptDetail>(`${BASE}/attempts/${id}/abandon`, { method: "POST" });
}

export function exportAttemptsCsv(f: Omit<AttemptFilters, "userId"> = {}) {
  return apiDownload(`${BASE}/attempts/export${query({ ...attemptQuery(f), format: "csv" })}`);
}

// --- reports -----------------------------------------------------------------

export function getSimSummary(opts: { courseMoodleId?: number; since?: string } = {}) {
  return apiFetch<SimSummary>(
    `${BASE}/reports/summary${query({ course_moodle_id: opts.courseMoodleId, since: opts.since })}`
  );
}
